<template>
    <aside id="elle-modal" ref="$modal" class="elle-modal" :class="{ show: isShow }">
        <div ref="$overflow" class="elle-modal--overflow" @click="showModal = false" />
        <div ref="$wrapper" class="elle-modal--wrapper">
            <button type="button" class="modal-close" :title="$t('site.label_close')" @click="showModal = false">
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect class="vert" x="15.75" width="1.5" height="33" fill="white" />
                    <rect class="horiz" y="17.2489" width="1.5" height="33" transform="rotate(-90 0 17.2489)" fill="white" />
                </svg>
            </button>
            <video ref="$video" :poster="poster.webo" controls playsinline loop :muted="false" preload="none">
                <source :src="video.url" type="video/mp4" />
                Your browser doesn't support HTML5 video tag.
            </video>
        </div>
    </aside>
</template>

<script setup lang="ts">
defineProps({
    poster: {
        type: Object,
        default: () => ({})
    },
    video: {
        type: Object,
        default: () => ({})
    }
});

const { $gsap: gsap, $ScrollTrigger: ScrollTrigger } = useNuxtApp();

const $modal = ref<HTMLDivElement | null | undefined>(null);
const $overflow = ref<HTMLDivElement | null | undefined>(null);
const $wrapper = ref<HTMLDivElement | null | undefined>(null);
const $video = ref<HTMLVideoElement | null | undefined>(null);
const isShow = ref(false);
const showModal = useShowModal();

const animationTimeline = ref<GSAPAnimation | null>(null);
let videoTrigger: { kill: () => void } | null = null;

const playVideo = () => {
    const video = $video.value;
    if (!video || !isShow.value) {
        return;
    }

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => undefined);
    }
};

const pauseVideo = () => {
    $video.value?.pause();
};

useStartAnimations($modal.value!, () => {
    gsap.set($overflow.value!, {
        xPercent: 100
    });
    gsap.set($wrapper.value!, {
        scale: 0
    });

    animationTimeline.value = gsap
        .timeline({
            paused: true,
            onComplete: () => {
                $video.value!.muted = false;
                playVideo();
            },
            onReverseComplete: () => {
                $video.value!.muted = true;
                pauseVideo();
                isShow.value = false;
            }
        })
        .to($overflow.value!, {
            xPercent: 0,
            duration: 0.2,
            ease: 'power2.inOut'
        })

        .to($wrapper.value!, {
            scale: 1,
            duration: 0.75,
            ease: 'power2.inOut'
        });
});

watch(
    () => showModal.value,
    state => {
        if (state) {
            if ($video.value) {
                $video.value.currentTime = 0;
            }
            isShow.value = true;
            animationTimeline.value?.play();
        } else {
            animationTimeline.value?.reverse();
        }
    }
);

onMounted(() => {
    nextTick(() => {
        if ($video.value) {
            $video.value.volume = 0.5;
        }

        if (!ScrollTrigger || !$video.value) {
            return;
        }

        videoTrigger = ScrollTrigger.create({
            trigger: $video.value,
            start: 'top bottom',
            end: 'bottom top',
            onEnter: playVideo,
            onEnterBack: playVideo,
            onLeave: pauseVideo,
            onLeaveBack: pauseVideo
        });
    });
});

onBeforeUnmount(() => {
    videoTrigger?.kill();
});
</script>
